import { FormEvent, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Pencil, Trash2, Users, UserX, X } from 'lucide-react'
import { api } from '../lib/api'
import type { AppUser, FeishuUserBinding, UserRole } from '../types'

const responsibleRoles: UserRole[] = ['SUPER_ADMIN', 'USER_ADMIN', 'APPROVER', 'LICENSE_ISSUER', 'LICENSE_OPERATOR']

const roleLabels: Record<UserRole, string> = {
  SUPER_ADMIN: '平台超级管理员', USER_ADMIN: '平台管理员', APPROVER: 'License 审批人',
  LICENSE_ISSUER: 'License 签发人', LICENSE_OPERATOR: 'License 申请人',
}

const roleDescriptions: Partial<Record<UserRole, string>> = {
  SUPER_ADMIN: '承担平台超级管理员岗位的全部治理业务，包括平台配置、权限治理、审计证据、通知诊断和密钥控制。',
  USER_ADMIN: '承担平台管理员岗位的人员治理业务，管理平台管理员及 License 业务岗位；超级管理员由超级管理员单独治理。',
  APPROVER: '承担 License 审批人岗位的全部业务，包括标准与特殊申请审批、驳回、催办复核和审批异常跟进。',
  LICENSE_ISSUER: '承担 License 签发人岗位的全部业务，包括待签发处理、正式签发、Artifact 生成和签发异常跟进。',
  LICENSE_OPERATOR: '承担 License 申请人岗位的全部业务，包括客户与授权维护、申请、License 下载交付、激活交付、离线激活请求、响应、回执和后续跟进。',
}

type ResponsiblePersonEditForm = {
  displayName: string
  username: string
  password: string
  email: string
  openId: string
  role: UserRole
  enabled: boolean
}

const emptyForm: ResponsiblePersonEditForm = {
  displayName: '', username: '', password: '', email: '', openId: '', role: 'APPROVER', enabled: true,
}

export function FeishuResponsibilityPeoplePanel({ users, bindings, currentRole, onUsersChange, onOffboard, onDelete }: {
  users: AppUser[]
  bindings: FeishuUserBinding[]
  currentRole?: UserRole | string
  onUsersChange?: () => Promise<void> | void
  onOffboard?: (user: AppUser) => void
  onDelete?: (user: AppUser) => void
}) {
  const [editing, setEditing] = useState<AppUser>()
  const [form, setForm] = useState<ResponsiblePersonEditForm>(emptyForm)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const isSuperAdmin = currentRole === 'SUPER_ADMIN'
  const editableRoles = isSuperAdmin ? responsibleRoles : responsibleRoles.filter(role => role !== 'SUPER_ADMIN')

  const activeBindingByUsername = useMemo(() => new Map(bindings
    .filter(binding => binding.status === 'ACTIVE' && binding.username)
    .map(binding => [binding.username as string, binding])), [bindings])
  const managedUsers = useMemo(() => [...users]
    .sort((left, right) => left.role.localeCompare(right.role)
      || left.displayName.localeCompare(right.displayName, 'zh-CN')), [users])
  const responsibleUsers = managedUsers.filter(user => responsibleRoles.includes(user.role))
  const reachableCount = responsibleUsers.filter(user => activeBindingByUsername.has(user.username) && user.enabled).length
  const emailCount = responsibleUsers.filter(user => Boolean(user.email) && user.enabled).length

  function openEdit(user: AppUser) {
    setEditing(user)
    setForm({
      displayName: user.displayName, username: user.username, password: '', email: user.email || '',
      openId: activeBindingByUsername.get(user.username)?.openId || '', role: user.role, enabled: user.enabled,
    })
    setPasswordVisible(false); setError('')
  }

  async function submit(event: FormEvent) {
    if (!editing) return
    event.preventDefault(); setSaving(true); setError('')
    try {
      await api.updateFeishuResponsibilityPerson(editing.id, {
        user: {
          displayName: form.displayName.trim(), username: form.username.trim(), role: form.role,
          email: form.email.trim(), enabled: form.enabled, ...(form.password ? { password: form.password } : {}),
        },
        openId: form.openId.trim() || undefined,
      })
      await onUsersChange?.()
      setEditing(undefined)
    } catch (value) { setError(value instanceof Error ? value.message : '飞书任务负责人保存失败') }
    finally { setSaving(false) }
  }

  return <section className="panel feishu-responsibility-panel">
    <div className="panel-heading"><div><h3>飞书任务负责人</h3><p>统一管理负责人的岗位、通知渠道、登录状态与离岗交接；请使用页面顶部“新增用户”创建负责人。</p></div></div>
    <div className="responsibility-help-strip"><Users size={18} /><div><b>新增用户后，即进入对应岗位的统一负责人池</b><span>历史事件、客户和产品路由规则已全部停用。系统只在同岗位、账号可用、至少一个个人通知渠道可达且满足职责分离的人员中稳定分配任务。</span></div><em>{reachableCount} 人可收飞书 · {emailCount} 人可收邮件</em></div>
    {error && !editing && <div className="error-banner">{error}</div>}
    {managedUsers.length ? <div className="responsibility-people-list" role="list" aria-label="飞书任务负责人列表">{managedUsers.map(user => {
      const binding = activeBindingByUsername.get(user.username)
      const protectedSuperAdmin = user.role === 'SUPER_ADMIN' && !isSuperAdmin
      return <article className={`responsibility-person-card ${user.enabled ? '' : 'responsibility-person-card--disabled'}`} key={user.id} role="listitem">
        <div className="responsibility-person-identity">
          <span className="responsibility-person-avatar"><Users size={18} /></span>
          <div><b>{user.displayName}</b><small className="mono">{user.username}</small></div>
        </div>
        <div className="responsibility-person-role">
          <small className="responsibility-field-label">负责角色</small>
          <span className="type-tag">{roleLabels[user.role]}</span>
          <p>{roleDescriptions[user.role]}</p>
        </div>
        <div className="responsibility-person-channels">
          <small className="responsibility-field-label">个人通知渠道</small>
          <div className="responsibility-channel-list">
            <span className={`responsibility-binding ${binding ? 'responsibility-binding--ready' : 'responsibility-binding--missing'}`}>
              {binding ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              <b>{binding ? '飞书已绑定' : '飞书未绑定'}</b>
              <small>{binding ? (binding.displayName || binding.openId) : '暂不能接收飞书待办'}</small>
            </span>
            <span className={`responsibility-binding ${user.email ? 'responsibility-binding--ready' : 'responsibility-binding--missing'}`}>
              {user.email ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              <b>{user.email ? '邮箱已配置' : '邮箱未配置'}</b>
              <small>{user.email || '暂不能接收邮件待办'}</small>
            </span>
          </div>
        </div>
        <div className="responsibility-person-control">
          <div className="responsibility-person-status">
            <span className={`status ${user.enabled ? 'status--success' : 'status--neutral'}`}><i />{user.enabled ? '可登录并参与分配' : '已停用'}</span>
            <small>创建于 {new Date(user.createdAt).toLocaleDateString('zh-CN')}</small>
          </div>
          <div className="row-actions">{protectedSuperAdmin ? <span className="type-tag" title="只有超级管理员可以管理超级管理员账号">仅超级管理员可管理</span> : <><button type="button" className="button button--ghost responsibility-edit-button" title={`修改负责人：${user.displayName}`} aria-label={`修改负责人：${user.displayName}`} onClick={() => openEdit(user)}><Pencil size={15} />修改</button>{user.enabled ? <button type="button" className="icon-button icon-button--danger" title="离岗禁用" aria-label={`离岗禁用${user.displayName}`} onClick={() => onOffboard?.(user)}><UserX size={16} /></button> : <button type="button" className="icon-button icon-button--danger" title="删除负责人" aria-label={`删除${user.displayName}`} onClick={() => onDelete?.(user)}><Trash2 size={16} /></button>}</>}</div>
        </div>
      </article>
    })}</div> : <p className="muted-panel-text">暂未创建平台治理、审批、签发或 License 申请负责人。请使用页面顶部“新增用户”统一创建。</p>}

    {editing && <div className="modal-layer"><div className="modal responsibility-person-modal" role="dialog" aria-modal="true" aria-labelledby="responsibility-person-title"><div className="modal-heading"><div><small>飞书任务人员账号</small><h3 id="responsibility-person-title">修改飞书任务负责人</h3></div><button type="button" className="icon-button" aria-label="关闭" onClick={() => setEditing(undefined)}><X size={19} /></button></div><form className="form-grid" onSubmit={submit}>
      <div className="responsibility-edit-context span-2"><Pencil size={17} /><div><b>正在修改：{editing.displayName}</b><span>可修改角色、姓名、登录账号、工作邮箱和密码。变更角色或登录账号后，系统会撤销其历史会话，并按新岗位重新分配未完成任务。</span></div></div>
      <fieldset className="responsibility-role-picker span-2"><legend>负责角色</legend><div role="group" aria-label="负责角色">{editableRoles.map(role => <button type="button" key={role} className={form.role === role ? 'is-selected' : ''} aria-pressed={form.role === role} onClick={() => setForm({ ...form, role })}><b>{roleLabels[role]}</b><small>{roleDescriptions[role]}</small></button>)}</div><p>{isSuperAdmin ? '超级管理员可调整全部正式岗位。' : '平台管理员只能调整平台管理员及 License 业务岗位，不能授予超级管理员角色。'}</p></fieldset>
      <label>负责人姓名<input value={form.displayName} onChange={event => setForm({ ...form, displayName: event.target.value })} required maxLength={80} placeholder="例如 张三" /><small>用于任务卡片、人员列表和审计记录展示。</small></label>
      <label>登录账号<input className="mono" value={form.username} onChange={event => setForm({ ...form, username: event.target.value })} required maxLength={64} placeholder="例如 zhangsan" autoComplete="off" /><small>负责人登录 License Center 使用，保存后需按审计要求保留。</small></label>
      <label>工作邮箱（选填）<input type="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} maxLength={254} placeholder="name@example.com" /><small>配置 SMTP 后，当前待办会同时定向发送到该邮箱。</small></label>
      <label>飞书 open_id（选填）<input className="mono" value={form.openId} onChange={event => setForm({ ...form, openId: event.target.value })} maxLength={128} placeholder="例如 ou_xxx 或 open_xxx" autoComplete="off" /><small>已绑定时会回显当前值；修改后将受控替换原绑定，留空则保持当前绑定状态。</small></label>
      <label>重置密码（选填）<span className="password-field"><input type={passwordVisible ? 'text' : 'password'} value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} minLength={8} maxLength={72} placeholder="留空则保持原密码" autoComplete="new-password" /><button type="button" className="password-toggle" onClick={() => setPasswordVisible(value => !value)} aria-label={passwordVisible ? '隐藏密码' : '显示密码'}>{passwordVisible ? <EyeOff size={17} /> : <Eye size={17} />}</button></span><small>只有需要重置时才填写。</small></label>
      <div className="responsibility-role-preview span-2"><b>{roleLabels[form.role]} · 全部岗位业务</b><p>{roleDescriptions[form.role]}</p><small>选择后，该人员按角色承担上述全部业务，不需要再配置具体事件或业务环节。请确保至少一个个人通知渠道可用。</small></div>
      <label className="switch-row span-2"><input type="checkbox" checked={form.enabled} onChange={event => setForm({ ...form, enabled: event.target.checked })} /><span><b>允许登录并参与任务分配</b><small>停用后该人员不能登录，也不会再被分配新的飞书任务。</small></span></label>
      {error && <div className="form-error span-2">{error}</div>}
      <div className="modal-actions span-2"><button type="button" className="button button--ghost" onClick={() => setEditing(undefined)}>取消</button><button className="button button--primary" disabled={saving}>{saving ? '正在保存...' : '保存修改'}</button></div>
    </form></div></div>}
  </section>
}
